import {
  byCustomerToken,
  defineFlagsManifest,
  flag,
  FlagsFromManifest,
  targetingRulesFromManifest,
} from '@cash-web/shared-util-feature-targeting'

export const featureFlagDefaultStringVariantValues = ['UNASSIGNED', 'ENABLED', 'DISABLED'] as const

export type FeatureFlagDefaultStringVariant = (typeof featureFlagDefaultStringVariantValues)[number]

const timelineStageValues = [
  'PRE_SEASON',
  'TAX_SEASON_START',
  'EMPLOYER_FORMS_SENT',
  'INVESTMENT_FORMS_SENT',
  'EFILE_DEADLINE',
  'POST_SEASON',
] as const

type TimelineStage = (typeof timelineStageValues)[number]

const fdicDisclosureVariationValues = [
  'PREPAID',
  'PREPAID_DEBITFLEX_SUTTON',
  'PREPAID_DEBITFLEX_SUTTON_BANCORP',
] as const

export type TaxesArticleName =
  | 'how-to-check-your-refund'
  | 'refund-tips'
  | 'things-to-know'
  | 'what-goes-into-your-refund'
  | 'whats-new'
  | 'what-to-do-if-you-owe'
  | 'why-cash-app-taxes'

export type ArcadeComponentMigrationFlags =
  | 'cash-web-arcade-migration-apps'
  | 'cash-web-arcade-migration-button'
  | 'cash-web-arcade-migration-input'
  | 'cash-web-arcade-migration-padding-inset'
  | 'cash-web-arcade-migration-avatar'
  | 'cash-web-arcade-migration-loading-image'
  | 'cash-web-arcade-migration-loading-icon'

export type ArcadeComponentMigrationConfig = {
  [K in ArcadeComponentMigrationFlags]?: FeatureFlagDefaultStringVariant
}

export enum SeverityLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export type OutageBannerIncident = {
  details?: string
  id?: string
  reportedAt?: number
  severityLevel?: SeverityLevel
  status?: string
  title?: string
}

export type Incidents = {
  incidents?: OutageBannerIncident[]
}

export type AccountSwitcherVersion = {
  minAndroidAppVersion: string
  minIOSAppVersion: string
}

type RumConfig = { sessionSampleRateOverride: number }

type TaxesBtcImportTopOfFunnelType = {
  enabled?: boolean
  taxesAppletBannerCopy?: string
}

export const featureFlagsManifest = defineFlagsManifest({
  'cash-frontend-product-card-studio-kiosk': flag.boolean(),
  'cash-frontend-product-referrals-friendsgiving': flag.boolean(),
  'cash-frontend-product-resend-code': flag.boolean(),
  'cash-frontend-product-tag-ordering': flag.boolean(),
  'cash-frontend-service-default-outage-status-message': flag.string(),
  'cash-frontend-tax-upgrade': flag.boolean(),
  'cash-web-3ds-redirect-url-test': flag.string(),
  'cash-web-account-address-autocomplete': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-allow-international-p2p': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-bitcoin-limits': flag.boolean(byCustomerToken),
  'cash-web-account-cash-limits-xp': flag.boolean(byCustomerToken),
  'cash-web-account-datadog-rum-config': flag.json<RumConfig>(byCustomerToken),
  'cash-web-account-datadog-rum-enabled': flag.boolean(byCustomerToken),
  'cash-web-account-edit-distributions': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-enable-instrument-selection-blocker-v3': flag.boolean(byCustomerToken),
  'cash-web-account-enable-multi-allocation-blocker': flag.boolean(byCustomerToken),
  'cash-web-account-enable-paychecks-stock-editing': flag.boolean(byCustomerToken),
  'cash-web-account-enable-sid': flag.boolean(byCustomerToken),
  'cash-web-account-enable-tm': flag.boolean(byCustomerToken),
  'cash-web-account-genie-scenes': flag.boolean(byCustomerToken),
  'cash-web-account-intelligent-applets': flag.boolean(byCustomerToken),
  'cash-web-account-suggested-actions': flag.boolean(byCustomerToken),
  'cash-web-account-janus-contact-aliases': flag.boolean(byCustomerToken),
  'cash-web-account-locale-disclosure': flag.boolean(byCustomerToken),
  'cash-web-account-multiple-goals-paychecks-enabled': flag.boolean(byCustomerToken),
  'cash-web-account-mweb-home': flag.boolean(byCustomerToken),
  'cash-web-account-new-sam-settings': flag.boolean(byCustomerToken),
  'cash-web-account-paychecks': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-paychecks-nux': flag.boolean(byCustomerToken),
  'cash-web-account-personal-data-download-endpoint': flag.boolean(byCustomerToken),
  'cash-web-account-roundup-upsells': flag.boolean(byCustomerToken),
  'cash-web-account-sam': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-savings-fetch-sponsorship': flag.boolean(byCustomerToken),
  'cash-web-account-settings-instrument-link-overrides': flag.json<{
    allowBankLink?: boolean
    allowCreditLink?: boolean
    allowDebitLink?: boolean
  }>(byCustomerToken),
  'cash-web-account-settings-show-devices': flag.boolean(byCustomerToken),
  'cash-web-account-settings-square-alias': flag.boolean(byCustomerToken),
  'cash-web-account-show-money-tab-uk': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-show-p2p-for-uk': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-show-restricted-balance': flag.boolean(byCustomerToken),
  'cash-web-account-tag-status-display': flag.boolean(byCustomerToken),
  'cash-web-account-taxes-banner': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-taxes-entrypoint-in-sidebar-nav': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-account-verify-alias-additional-help-items': flag.boolean(byCustomerToken),
  'cash-web-account-waitlist-heart': flag.boolean(byCustomerToken),
  'cash-web-account-waitlist-minicard': flag.boolean(byCustomerToken),
  'cash-web-admin-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-arcade-feature-documents': flag.enum(featureFlagDefaultStringVariantValues),
  'cash-web-arcade-feature-pay-and-request': flag.enum(featureFlagDefaultStringVariantValues),
  'cash-web-arcade-migration': flag.json<ArcadeComponentMigrationConfig>(),
  'cash-web-cashtags-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-conversion-tracking': flag.boolean(),
  'cash-web-device-details-endpoint': flag.boolean(),
  'cash-web-enable-risk-signal': flag.boolean(),
  'cash-web-enable-sid-retry': flag.boolean(),
  'cash-web-enable-sitm': flag.boolean(),
  'cash-web-enable-tm': flag.boolean(),
  'cash-web-enable-wait-for-sid': flag.boolean(),
  'cash-web-get-profile-migration': flag.boolean(byCustomerToken),
  'cash-web-grant-flow-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-grant-flow-enable-alias-back-button': flag.boolean(),
  'cash-web-incidents': flag.json<Incidents>(),
  'cash-web-lightning-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-limits-use-xp-for-btc': flag.boolean(byCustomerToken),
  'cash-web-local-profile-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-local-profile-use-one-trust': flag.boolean(),
  'cash-web-login-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-login-otp-auto-submit': flag.boolean(),
  'cash-web-moneybot-cached-session-id-timeout-seconds': flag.number(),
  'cash-web-moneybot-debug-copy-session': flag.boolean(),
  'cash-web-moneybot-developer-config': flag.boolean(),
  'cash-web-moneybot-home-visual-field': flag.boolean(),
  'cashclient-moneybot-frontdoor-loading-delay-milliseconds': flag.number(),
  'cashclient-moneybot-loading-delay-milliseconds': flag.number(),
  'cash-web-neighborhood-profiles-order-review-modal': flag.boolean(),
  'cash-web-neighborhoods-app-handoff-prompt': flag.boolean(),
  'cash-web-neighborhoods-checkout-buyer-offers': flag.boolean(),
  'cash-web-neighborhoods-enable-tm': flag.boolean(),
  'cash-web-neighborhoods-local-page-v3': flag.boolean(),
  'cash-web-neighborhoods-multi-vertical-checkout': flag.boolean(),
  'cash-web-neighborhoods-multi-vertical-confirmation': flag.boolean(),
  'cash-web-neighborhoods-multi-vertical-profile': flag.boolean(),
  'cash-web-one-trust-banner-visibility': flag.boolean(),
  'cash-web-p2p-misdirected-payment-in-flow': flag.enum(featureFlagDefaultStringVariantValues),
  'cash-web-passkeys': flag.enum(featureFlagDefaultStringVariantValues),
  'cash-web-passkeys-try-login-with-passkeys': flag.enum(featureFlagDefaultStringVariantValues),
  'cash-web-pay-links-single-pay-cta': flag.boolean(),
  'cash-web-personalized-ads-enabled': flag.boolean(),
  'cash-web-pools-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-pools-enable-contributions': flag.boolean(),
  'cash-web-shadow-account-debit-text': flag.enum(featureFlagDefaultStringVariantValues),
  'cash-web-show-money-tab-settings': flag.json<{
    countries?: ReadonlyArray<string>
  }>(),
  'cash-web-standalone-pages-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-support-datadog-rum-config': flag.json<RumConfig>(),
  'cash-web-support-datadog-rum-enabled': flag.boolean(),
  'cash-web-taxes-account-switcher-version': flag.json<AccountSwitcherVersion>(byCustomerToken),
  'cash-web-taxes-article-list': flag.json<Record<TaxesArticleName, boolean>>(byCustomerToken),
  'cash-web-taxes-btc-import-top-of-funnel': flag.json<TaxesBtcImportTopOfFunnelType>(byCustomerToken),
  'cash-web-taxes-datadog-rum-config': flag.json<RumConfig>(byCustomerToken),
  'cash-web-taxes-desktop-applet': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-taxes-experience-service': flag.boolean(byCustomerToken),
  'cash-web-taxes-open-for-data-entry': flag.enum(featureFlagDefaultStringVariantValues, byCustomerToken),
  'cash-web-taxes-sam-restriction': flag.boolean(byCustomerToken),
  'cash-web-taxes-timeline-event-dates': flag.json<Record<TimelineStage, string>>(byCustomerToken),
  'cash-web-taxes-timeline-stage': flag.enum(timelineStageValues, byCustomerToken),
  'cash-web-weak-pin-client-check': flag.boolean(byCustomerToken),
  'fdic-disclosure-variation': flag.enum(fdicDisclosureVariationValues),
} as const)

export type FeatureFlags = FlagsFromManifest<typeof featureFlagsManifest>

export const featureFlagsTargetingRules = targetingRulesFromManifest(featureFlagsManifest)
